import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';

import { ApiStatus } from 'common/enums';
import { IErrorResponse } from 'common/models/api-service/error-response.model';
import { PaginatedApiResponse } from 'common/models/api-service/api-response.model';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { EntityVM, PreviewEntityDTO } from 'common/models/data/entity.model';
import {
    addCategories,
    addCategoriesByHierarchyId,
    findCategories,
    findCategoriesByHierarchyId,
    removeCategories,
    setRadioButton,
    toggleExpansion,
    updateCategory,
    updateSubCategoryByHierarchyId
} from 'common/utils/table';

import initialArchiveState from './archive.state';
import { ArchiveListBySearchRequest, ArchiveListRequest } from '../models/archive.model';
import { ApiInitiator, EntityType } from 'common/enums/data.enum';
import { SearchVM } from 'common/models';

const archiveSlice = createSlice({
    name: 'archive',
    initialState: initialArchiveState,
    reducers: {
        toggleExpansion: (state, { payload }: PayloadAction<EntityVM>) => {
            if (state.archiveList.data?.data)
                state.archiveList.data.data = toggleExpansion(
                    payload,
                    state.archiveList.data?.data,
                    ApiInitiator.ArchiveList
                );
        },
        toggleExpansionSuccess: (
            state,
            { payload }: PayloadAction<{ parent: EntityVM; subCategories: EntityVM[] }>
        ) => {
            const entity = findCategories(state.archiveList.data?.data, payload.parent.id);
            if (
                entity.subCategories === undefined ||
                (entity.subCategories && entity.subCategories.length === 0)
            ) {
                if (state.archiveList.data?.data) {
                    state.archiveList.data.data = addCategories(
                        state.archiveList.data?.data,
                        payload.parent.id,
                        payload.subCategories
                    );
                }
            }
        },
        toggleExpansionFailed: (state, { payload }: PayloadAction<PreviewEntityDTO>) => {
            state.archiveList.status = ApiStatus.ERROR;
        },
        fetchArchiveList: (state, { payload }: PayloadAction<ArchiveListRequest>) => {
            state.archiveList.status = ApiStatus.LOADING;
        },
        fetchArchiveListSuccess: (
            state,
            { payload }: PayloadAction<PaginatedApiResponse<EntityVM[]>>
        ) => {
            state.archiveList.status = ApiStatus.SUCCESS;
            if (payload.pagination.currentPage === 1) {
                state.archiveList.data = payload;
            } else {
                if (
                    state.archiveList.data &&
                    state.archiveList.data.data &&
                    state.archiveList.data.data.length > 0 &&
                    payload.data &&
                    payload.data.length > 0
                ) {
                    let updatedData = [...current(state.archiveList.data.data)];
                    updatedData = updatedData.concat([...payload.data]);
                    state.archiveList.data.data = updatedData;
                    state.archiveList.data.pagination = payload.pagination;
                }
            }
        },
        fetchArchiveListError: (state) => {
            state.archiveList.status = ApiStatus.ERROR;
        },
        deleteArchiveDataBegin: (state, { payload }: PayloadAction<any>) => {
            state.deleteArchivedData.status = ApiStatus.LOADING;
        },
        deleteArchiveDataSuccess: (state, { payload }: PayloadAction<any>) => {
            state.deleteArchivedData.status = ApiStatus.SUCCESS;
            if (state.archiveList.data)
                state.archiveList.data.data = removeCategories(
                    state.archiveList.data?.data,
                    payload.entityId
                );
        },
        deleteArchiveDataError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.deleteArchivedData.status = ApiStatus.ERROR;
            state.deleteArchivedData.error = payload;
        },
        searchList: (state, { payload }: PayloadAction<any>) => {
            state.searchList.status = ApiStatus.LOADING;
        },
        searchListSuccess: (state, { payload }: PayloadAction<any>) => {
            state.searchList.status = ApiStatus.SUCCESS;
            state.searchList.data = payload;
        },
        searchListError: (state) => {
            state.searchList.status = ApiStatus.ERROR;
        },
        resetHierarchicalData: (state) => {
            state.hierarchicalData = {};
        },
        setRadioButtonSelected: (state, { payload }: PayloadAction<any>) => {
            if (
                state.hierarchicalData.data &&
                state.hierarchicalData.data.data &&
                state.hierarchicalData.data?.data.length > 0
            ) {
                state.hierarchicalData.data.data = setRadioButton(
                    state.hierarchicalData.data.data,
                    payload.targetId,
                    payload.field
                );
            }
        },
        getHierarchicalData: (
            state,
            {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                payload
            }: PayloadAction<{ pagedRequest: PagedRequest; entityId: string; type: EntityType }>
        ) => {
            state.hierarchicalData.status = ApiStatus.LOADING;
        },
        getHierarchicalDataSuccess: (state, { payload }: PayloadAction<any>) => {
            state.hierarchicalData.status = ApiStatus.SUCCESS;
            if (payload.archivedDataList.pagination.currentPage === 1) {
                state.hierarchicalData.data = payload.archivedDataList;
                if (state.hierarchicalData.data)
                    state.hierarchicalData.data.path = payload.archivedHierarchyPath;
            } else {
                if (
                    state.hierarchicalData.data &&
                    state.hierarchicalData.data.data &&
                    state.hierarchicalData.data.data.length > 0 &&
                    payload.archivedDataList.data &&
                    payload.archivedDataList.data.length > 0
                ) {
                    let updatedData = [...current(state.hierarchicalData.data.data)];
                    updatedData = updatedData.concat([...payload.archivedDataList.data]);
                    state.hierarchicalData.data.data = updatedData;
                    state.hierarchicalData.data.pagination = payload.archivedDataList.pagination;
                }
            }
        },
        getHierarchicalDataError: (state, { payload }: PayloadAction<any>) => {
            state.hierarchicalData.status = ApiStatus.ERROR;
            state.hierarchicalData.error = payload.archivedDataList.errors;
        },
        toggleHierarchicalDataExpansion: (state, { payload }: PayloadAction<EntityVM>) => {
            if (state.hierarchicalData.data?.data)
                state.hierarchicalData.data.data = toggleExpansion(
                    payload,
                    state.hierarchicalData.data?.data,
                    ApiInitiator.MasterData
                );
        },
        toggleHierarchicalDataExpansionSuccess: (
            state,
            { payload }: PayloadAction<{ parent: EntityVM; subCategories: EntityVM[] }>
        ) => {
            const entity = findCategories(state.hierarchicalData.data?.data, payload.parent.id);
            if (
                entity.subCategories === undefined ||
                (entity.subCategories && entity.subCategories.length === 0)
            ) {
                if (state.hierarchicalData.data?.data) {
                    state.hierarchicalData.data.data = addCategories(
                        state.hierarchicalData.data?.data,
                        payload.parent.id,
                        payload.subCategories
                    );
                }
            }
        },
        toggleHierarchicalDataExpansionFailed: (
            state,
            { payload }: PayloadAction<PreviewEntityDTO>
        ) => {
            return state;
        },
        restore: (state) => {
            state.restoreData.status = ApiStatus.LOADING;
        },
        restoreSuccess: (state) => {
            state.restoreData.status = ApiStatus.SUCCESS;
        },
        restoreFailed: (state) => {
            state.restoreData.status = ApiStatus.ERROR;
        },
        setRestoredData: (state, { payload }: PayloadAction<any>) => {
            state.dataToBeRestored = payload;
        },
        resetState: () => {
            return initialArchiveState;
        },
        setHierarchicalData: (state) => {
            state.hierarchicalData.status = ApiStatus.SUCCESS;
            state.hierarchicalData = { ...state.archiveList };
        },
        refreshEntitiesByCategory: (
            state,
            { payload }: PayloadAction<{ entityType: EntityType; entityId: string }>
        ) => {
            state.archiveList = { ...state.archiveList, status: ApiStatus.LOADING };
        },
        refreshEntitiesByCategorySuccess: (
            state,
            {
                payload
            }: PayloadAction<{
                parent: { entityType: EntityType; entityId: string };
                subCategories: EntityVM[];
            }>
        ) => {
            let entity: any = {};
            if (payload.parent.entityType === EntityType.Category)
                entity = findCategories(state.archiveList.data?.data, payload.parent.entityId);
            else
                entity = findCategoriesByHierarchyId(
                    state.archiveList.data?.data,
                    payload.parent.entityId
                );
            if (entity) {
                entity.subCategories = [];
                if (!entity.hasChild && payload.subCategories.length > 0) entity.hasChild = true;
                if (state.archiveList.data?.data) {
                    if (payload.parent.entityType === EntityType.Category) {
                        state.archiveList.data.data = updateCategory(
                            state.archiveList.data.data,
                            entity.id,
                            entity
                        );
                        state.archiveList.data.data = addCategories(
                            state.archiveList.data?.data,
                            entity.id,
                            payload.subCategories
                        );
                    } else {
                        state.archiveList.data.data = updateSubCategoryByHierarchyId(
                            state.archiveList.data.data,
                            entity.hierarchyId,
                            entity
                        );

                        if (payload.subCategories && payload.subCategories.length > 0) {
                            payload.subCategories.forEach(
                                (sc: EntityVM) => (sc.parentHierarchyId = entity.hierarchyId)
                            );
                        }
                        state.archiveList.data.data = addCategoriesByHierarchyId(
                            state.archiveList.data?.data,
                            entity.hierarchyId,
                            payload.subCategories
                        );
                    }
                }
            }
        },
        refreshEntitiesByCategoryFailed: (state, { payload }: PayloadAction<PreviewEntityDTO>) => {
            state.archiveList.status = ApiStatus.ERROR;
        },
        fetchArchiveListBySearchBegin: (
            state,
            { payload }: PayloadAction<ArchiveListBySearchRequest>
        ) => {
            state.searchList.status = ApiStatus.LOADING;
        },
        fetchArchiveListBySearchSuccess: (
            state,
            { payload }: PayloadAction<PaginatedApiResponse<SearchVM[]>>
        ) => {
            state.searchList.status = ApiStatus.SUCCESS;
            if (payload.pagination.currentPage === 1) {
                state.searchList.data = payload;
            } else {
                if (
                    state.searchList.data &&
                    state.searchList.data.data &&
                    state.searchList.data.data.length > 0 &&
                    payload.data &&
                    payload.data.length > 0
                ) {
                    let updatedData = [...current(state.searchList.data.data)];
                    updatedData = updatedData.concat([...payload.data]);
                    state.searchList.data.data = updatedData;
                    state.searchList.data.pagination = payload.pagination;
                }
            }
        },
        fetchArchiveListBySearchError: (state) => {
            state.searchList.status = ApiStatus.ERROR;
        }
    }
});

export default archiveSlice.reducer;
export const archiveActions = archiveSlice.actions;
